import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  useDispatch,
  useSelector,
} from "react-redux";

import {
  Link,
} from "react-router-dom";

import {
  Badge,
  Box,
  Flex,
  Heading,
  Icon,
  Input,
  LinkBox,
  LinkOverlay,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
} from "@chakra-ui/react";

import {
  FiArchive,
  FiArrowRight,
  FiSearch,
} from "react-icons/fi";

import {
  listenToProjects,
} from "./projectActions";

const getStatusColor = (status) => {
  switch (status) {
    case "Open":
      return "green";

    case "Agenda Item For Next Meeting":
      return "yellow";

    case "Consultor’s Approved":
    case "Administratively Approved":
      return "blue";

    case "Consultor’s Denied":
      return "red";

    case "Closed":
      return "gray";

    default:
      return "gray";
  }
};
const getPriorityColor = (priority) => {
  switch (priority) {
    case "Low":
      return "green";

    case "Normal":
      return "blue";

    case "High":
      return "orange";

    case "Urgent":
      return "red";

    case "Unassigned":
    default:
      return "gray";
  }
};

const truncateTitle = (
  title,
  maxLength = 45
) => {
  if (!title) {
    return "Untitled Project";
  }

  return title.length > maxLength
    ? `${title.slice(0, maxLength)}…`
    : title;
};

const getProjectNumber = (project) => {
  return (
    project.projectNumber ||
    project.id?.slice(0, 9) ||
    "Unavailable"
  );
};

const normalizeEmail = (email) =>
  typeof email === "string"
    ? email.trim().toLowerCase()
    : "";

const EmptySection = ({
  searchActive,
  sectionName,
}) => (
  <Box
    p={6}
    mb={10}
    bg="gray.50"
    borderRadius="md"
    borderWidth="1px"
  >
    <Text color="gray.600">
      {searchActive
        ? `No ${sectionName.toLowerCase()} match your search.`
        : `There are no ${sectionName.toLowerCase()}.`}
    </Text>
  </Box>
);

const Projects = () => {
  const dispatch = useDispatch();

  const [searchTerm, setSearchTerm] =
    useState("");

  const { projects } = useSelector(
    (state) => state.projects
  );

  const { user } = useSelector(
    (state) => state.auth
  );

  const profile = useSelector(
    (state) => state.profile.profile
  );

  const { loading } = useSelector(
    (state) => state.async
  );

  const isAdmin =
    profile?.isAdmin === true;

  const email =
    normalizeEmail(user?.email);

  useEffect(() => {
    if (
      !email ||
      typeof profile?.isAdmin !==
        "boolean"
    ) {
      return undefined;
    }

    const unsubscribe = dispatch(
      listenToProjects({
        email,
        isAdmin,
      })
    );

    return () => {
      if (
        typeof unsubscribe ===
        "function"
      ) {
        unsubscribe();
      }
    };
  }, [
    dispatch,
    email,
    isAdmin,
    profile?.isAdmin,
  ]);

  const normalizedSearch =
    searchTerm
      .trim()
      .toLowerCase()
      .replace(/^#/, "");

  const visibleProjects = useMemo(
    () =>
      projects.filter((project) => {
        if (isAdmin) {
          return true;
        }

        return (
          normalizeEmail(
            project.createdBy?.email
          ) === email ||
          normalizeEmail(
            project.contactEmail
          ) === email ||
          project.additionalEmails?.some(
            (additionalEmail) =>
              normalizeEmail(
                additionalEmail
              ) === email
          )
        );
      }),
    [projects, isAdmin, email]
  );

  const filteredProjects = useMemo(
    () =>
      visibleProjects.filter(
        (project) => {
          if (!normalizedSearch) {
            return true;
          }

          const searchableValues = [
            getProjectNumber(project),
            project.projectTitle,
            project.parish,
            project.pastor,
            project.contactName,
            project.contactEmail,
            project.status,
            project.priority,
          ];

          return searchableValues.some(
            (value) =>
              String(value || "")
                .toLowerCase()
                .includes(
                  normalizedSearch
                )
          );
        }
      ),
    [
      visibleProjects,
      normalizedSearch,
    ]
  );

  const {
    openProjects,
    closedProjects,
    archivedProjects,
  } = useMemo(() => {
    const open = [];
    const closed = [];
    const archived = [];

    filteredProjects.forEach(
      (project) => {
        if (project.archived === true) {
          archived.push(project);
          return;
        }

        if (
          project.status === "Closed"
        ) {
          closed.push(project);
          return;
        }

        open.push(project);
      }
    );

    return {
      openProjects: open,
      closedProjects: closed,
      archivedProjects: archived,
    };
  }, [filteredProjects]);

  const renderProjectCard = (
    project,
    type = "open"
  ) => {
    const projectNumber =
      getProjectNumber(project);

    const isClosed =
      type === "closed";

    const isArchived =
      type === "archived";

    return (
      <LinkBox
        as="article"
        key={project.id}
        p={5}
        borderWidth="1px"
        borderColor={
          isArchived
            ? "orange.200"
            : "gray.200"
        }
        borderRadius="lg"
        shadow={
          isArchived || isClosed
            ? "sm"
            : "md"
        }
        bg={
          isArchived
            ? "orange.50"
            : isClosed
            ? "gray.50"
            : "white"
        }
        opacity={
          isClosed ? 0.9 : 1
        }
        transition="0.2s"
        _hover={{
          shadow: "lg",
          transform:
            "translateY(-2px)",
        }}
      >
        <Stack spacing={3}>
          <Stack spacing={1}>
            <Flex
              justify="space-between"
              align="center"
              gap={3}
            >
              <Text
                fontSize="xs"
                fontWeight="bold"
                color="gray.500"
                letterSpacing="wide"
              >
                PROJECT #{projectNumber}
              </Text>

              {isArchived && (
                <Icon
                  as={FiArchive}
                  color="orange.500"
                  aria-label="Archived project"
                />
              )}
            </Flex>

            <Heading
              size="md"
              noOfLines={2}
            >
              <LinkOverlay
                as={Link}
                to={`/projects/${project.id}`}
              >
                {truncateTitle(
                  project.projectTitle
                )}
              </LinkOverlay>
            </Heading>

            <Flex
  gap={2}
  flexWrap="wrap"
>
  {isArchived && (
    <Badge colorScheme="orange">
      Archived
    </Badge>
  )}

  <Badge
    colorScheme={getStatusColor(
      project.status || "Open"
    )}
  >
    {project.status || "Open"}
  </Badge>

  <Badge colorScheme="purple">
    Priority:{" "}
    {project.priority ||
      "Unassigned"}
  </Badge>
</Flex>
          </Stack>

          <Text
            fontSize="sm"
            color="gray.600"
          >
            Parish:{" "}
            {project.parish || "N/A"}
          </Text>

          <Text
            fontSize="sm"
            color="gray.600"
          >
            Pastor/Administrator:{" "}
            {project.pastor || "N/A"}
          </Text>

          <Flex
            justify="flex-end"
            mt={2}
          >
            <Icon
              as={FiArrowRight}
              w={5}
              h={5}
              color={
                isArchived
                  ? "orange.500"
                  : isClosed
                  ? "gray.500"
                  : "teal.500"
              }
            />
          </Flex>
        </Stack>
      </LinkBox>
    );
  };

  const renderSection = ({
    title,
    projectsToRender,
    type,
    sectionName,
  }) => (
    <Box mb={10}>
      <Heading
        size="lg"
        mb={4}
      >
        {title}{" "}
        <Text
          as="span"
          fontSize="md"
          fontWeight="normal"
          color="gray.500"
        >
          ({projectsToRender.length})
        </Text>
      </Heading>

      {projectsToRender.length > 0 ? (
        <SimpleGrid
          columns={{
            base: 1,
            md: 2,
            lg: 3,
          }}
          spacing={6}
        >
          {projectsToRender.map(
            (project) =>
              renderProjectCard(
                project,
                type
              )
          )}
        </SimpleGrid>
      ) : (
        <EmptySection
          searchActive={
            Boolean(normalizedSearch)
          }
          sectionName={sectionName}
        />
      )}
    </Box>
  );

  if (loading) {
    return (
      <Flex
        justify="center"
        align="center"
        minH="60vh"
      >
        <Spinner size="xl" />
      </Flex>
    );
  }

  return (
    <Box
      p={{
        base: 4,
        md: 6,
      }}
    >
      <Box
        maxW="650px"
        mb={8}
        position="relative"
      >
        <Icon
          as={FiSearch}
          position="absolute"
          left={4}
          top="50%"
          transform="translateY(-50%)"
          color="gray.400"
          pointerEvents="none"
          zIndex={1}
        />

        <Input
          value={searchTerm}
          onChange={(event) =>
            setSearchTerm(
              event.target.value
            )
          }
          placeholder="Search by project number, title, parish, contact, status, or priority"
          pl={14}
          bg="white"
          size="lg"
          aria-label="Search projects"
        />
      </Box>

      {normalizedSearch && (
        <Text
          mb={6}
          color="gray.600"
        >
          {filteredProjects.length}{" "}
          {filteredProjects.length === 1
            ? "project"
            : "projects"}{" "}
          found
        </Text>
      )}

      {renderSection({
        title: "Open Projects",
        projectsToRender:
          openProjects,
        type: "open",
        sectionName:
          "open projects",
      })}

      {renderSection({
        title: "Closed Projects",
        projectsToRender:
          closedProjects,
        type: "closed",
        sectionName:
          "closed projects",
      })}

      {renderSection({
        title: "Archived Projects",
        projectsToRender:
          archivedProjects,
        type: "archived",
        sectionName:
          "archived projects",
      })}
    </Box>
  );
};

export default Projects;